import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContractorAuthContext = createContext(null);

export const useContractorAuth = () => {
  const context = useContext(ContractorAuthContext);
  if (!context) {
    throw new Error('useContractorAuth must be used within ContractorAuthProvider');
  }
  return context;
};

export const ContractorAuthProvider = ({ children }) => {
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('contractor_token');
    const storedContractor = localStorage.getItem('contractor_info');
    
    if (storedToken && storedContractor) {
      setToken(storedToken);
      setContractor(JSON.parse(storedContractor));
    }
    setLoading(false);
  }, []);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('contractor_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  const login = async (contractorId, password) => {
    const response = await axios.post(`${API_URL}/api/contractors/login`, {
      contractor_id: contractorId.toUpperCase(),
      password
    });
    
    const { access_token, contractor: contractorData, agreement_signed } = response.data;
    
    localStorage.setItem('contractor_token', access_token);
    localStorage.setItem('contractor_info', JSON.stringify({
      ...contractorData,
      agreement_signed
    }));
    
    setToken(access_token);
    setContractor({ ...contractorData, agreement_signed });
    
    return { contractor: contractorData, agreement_signed };
  };

  const logout = () => {
    localStorage.removeItem('contractor_token');
    localStorage.removeItem('contractor_info');
    setToken(null);
    setContractor(null);
  };

  const refreshContractor = async () => {
    try {
      const response = await api.get('/contractors/agreement/status');
      if (contractor) {
        const updatedContractor = { 
          ...contractor, 
          agreement_signed: response.data.agreement_signed,
          contractor_status: response.data.contractor_status
        };
        setContractor(updatedContractor);
        localStorage.setItem('contractor_info', JSON.stringify(updatedContractor));
      }
    } catch (err) {
      console.error('Error refreshing contractor status:', err);
    }
  };

  const value = {
    contractor,
    token,
    loading,
    isAuthenticated: !!token,
    agreementSigned: contractor?.agreement_signed || false,
    api,
    login,
    logout,
    refreshContractor
  };

  return (
    <ContractorAuthContext.Provider value={value}>
      {children}
    </ContractorAuthContext.Provider>
  );
};
